import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { EditedBadge } from '@/components/EditedBadge'
import { EditPromptsButton } from '@/components/EditPromptsButton'
import { EditPromptsForm } from '@/components/EditPromptsForm'
import { EngineAnswer } from '@/components/EngineAnswer'
import { EngineBadge } from '@/components/EngineBadge'
import { ResponseMeta } from '@/components/ResponseMeta'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { durations } from '@/lib/motion/presets'
import { cn } from '@/lib/utils'
import type { QueryAccordionProps } from './QueryAccordion.types'

// The closing sibling and the opening row animate at once, which drags the page
// up. Scroll the opened row to the top once both have settled.
const SETTLE_MS = durations.panel * 1000 + 60

export function QueryAccordion({ run }: QueryAccordionProps) {
  const competitorNames = useMemo(
    () => run.competitors.map((c) => c.name),
    [run.competitors]
  )
  const prompts = useMemo(
    () => run.perQuery.map((q) => q.query),
    [run.perQuery]
  )
  const editState = run.promptEditing?.state
  const showButton = editState === 'tuning' || editState === 'locked'
  const [editing, setEditing] = useState(false)
  const [open, setOpen] = useState('')
  const list = useRef<HTMLDivElement>(null)
  const settle = useRef<number>(0)

  useEffect(() => () => window.clearTimeout(settle.current), [])

  const openRow = useCallback((value: string) => {
    setOpen(value)

    // A second click inside the settle window would otherwise scroll to the row
    // that just closed.
    window.clearTimeout(settle.current)

    if (!value) return

    settle.current = window.setTimeout(() => {
      list.current
        ?.querySelector(`[data-row="${value}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, SETTLE_MS)
  }, [])

  if (editing) {
    return (
      <EditPromptsForm
        runId={run.id}
        prompts={prompts}
        onDone={() => setEditing(false)}
      />
    )
  }

  return (
    <>
      {showButton && (
        <div className="mb-2 flex justify-end">
          <EditPromptsButton
            state={editState}
            onEdit={() => setEditing(true)}
          />
        </div>
      )}
      <Accordion
        ref={list}
        type="single"
        collapsible
        value={open}
        onValueChange={openRow}
        className="space-y-2"
      >
        {run.perQuery.map((q, i) => {
          const value = `q-${i}`
          const number = (
            <span className="w-6 shrink-0 tabular-nums text-xs text-muted-foreground">
              {String(i + 1).padStart(2, '0')}
            </span>
          )

          return (
            <AccordionItem
              key={value}
              value={value}
              data-row={value}
              className="min-w-0 max-w-full scroll-mt-4 rounded-lg border border-border bg-card px-4"
            >
              <AccordionTrigger
                className="min-w-0 hover:no-underline"
                leading={number}
              >
                <div className="@container/query-row flex min-w-0 w-full items-center gap-4 text-left">
                  <span
                    title={q.query}
                    className="min-w-0 flex-1 truncate text-sm font-medium"
                  >
                    {q.query}
                  </span>
                  {q.edited && <EditedBadge />}
                  <span
                    className={cn(
                      'mr-3 hidden shrink-0 items-center gap-1.5 sm:flex',
                      '[&_[data-slot=engine-badge-label]]:hidden',
                      '@min-[80rem]/query-row:[&_[data-slot=engine-badge-label]]:inline'
                    )}
                  >
                    {q.responses.map((r) => (
                      <EngineBadge
                        key={r.engineId}
                        engineId={r.engineId}
                        label={r.label}
                        mention={r.mention}
                      />
                    ))}
                  </span>
                </div>
              </AccordionTrigger>

              <AccordionContent className="pt-2">
                <Accordion
                  type="multiple"
                  defaultValue={q.responses.slice(0, 1).map((r) => r.engineId)}
                  className="divide-y divide-border/60"
                >
                  {q.responses.map((r) => (
                    <EngineAnswer
                      key={r.engineId}
                      response={r}
                      brand={run.brand}
                      competitors={competitorNames}
                      meta={<ResponseMeta response={r} />}
                    />
                  ))}
                </Accordion>
              </AccordionContent>
            </AccordionItem>
          )
        })}
      </Accordion>
    </>
  )
}
